import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjsUtils from '@attain-apps/rxjs-utils'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import type * as display from '../../../display'
import type * as dms from '../../../dms'
import {
  getDisplayEditsForValueChange,
  getReferenceTargetForValueChange,
} from '../display-bindings'
import type { Feature } from '../feature'

/**
 * Resolves external display-binding context for value edits and relays a
 * prepared local node patch back through `editNode`.
 */
export const onEditNodeValueResolveDisplayBindings = store.effects.create(
  (
    feature: Feature,
    schemaFeature: dms.schema.Feature,
    referenceRecordsFeature: display.referenceRecords.Feature,
  ) =>
    feature.actions.editNodeValue.observe().pipe(
      rxjsUtils.latestFrom(action => ({
        action: rxjs.of(action),
        chartMetadata: feature.selectors.getChartMetadata({
          chartId: action.data.chartId,
        }).asObservable,
        schema: schemaFeature.selectors.schema.asObservable,
      })),
      rxjs.mergeMap(({ action, chartMetadata, schema }) => {
        const {
          data: { attributeId, chartId, id, value },
        } = action

        if (TypeChecker.isNullish(id)) {
          return rxjs.of(store.effects.SKIP)
        }

        const attribute = schema?.tryGetAttribute(attributeId)
        const partialId =
          ents.core.dms.models.Id.Parts.from(attributeId).partialId

        const referenceTarget = getReferenceTargetForValueChange({
          attribute,
          bindings: chartMetadata?.displayBindings,
          partialId,
          schema,
          value,
        })

        const createEditNodeAction = (
          referenceRecord?: display.referenceRecords.models.Record,
        ) =>
          feature.actions.editNode.from(action).create({
            chartId,
            displayEdits: getDisplayEditsForValueChange({
              attribute,
              bindings: chartMetadata?.displayBindings,
              partialId,
              referenceRecord,
              schema,
              value,
            }),
            edits: {
              [partialId]: value,
            },
            id,
          })

        if (!referenceTarget) {
          return rxjs.of(createEditNodeAction())
        }

        return referenceRecordsFeature.selectors
          .getRecord(referenceTarget)
          .asObservable.pipe(
            rxjs.take(1),
            rxjs.map(referenceRecord => createEditNodeAction(referenceRecord)),
          )
      }),
    ),
)
